import { IconLoader2, IconSpeakerphone } from '@tabler/icons-react';
import { useMediaQuery } from '@/lib/hooks';
import BiggaponEditor from '../biggapon/BiggaponEditor';
import { useBiggaponSource } from '../biggapon/biggaponSource';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import EditorModalShell from './EditorModalShell';
import EditorWindow from './EditorWindow';

/**
 * The advertisement-poster editor in a full-screen window (the app's shared editor design) — use it anywhere.
 *
 *   <BiggaponModal open={open} onClose={() => setOpen(false)} />
 *
 * It loads the current job notices itself when it opens, and puts the active shop on the poster footer. Pick a notice and a
 * theme, change the wording, then download (JPG / PDF) or print. Edits are for the poster only and are not kept — the editor
 * starts fresh every time it opens (the chosen THEME is the viewer's usual saved one).
 */
export default function BiggaponModal({ open, onClose }) {
    const { t } = useTranslation();

    return (
        <EditorWindow open={open} onClose={onClose} title={t('biggapon.pageTitle')}>
            {/* its own component: the window mounts its content only while open, so every opening loads (or reuses) the notices anew */}
            <BiggaponWindow onClose={() => onClose?.()} />
        </EditorWindow>
    );
}

function BiggaponWindow({ onClose }) {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 767px)');
    const { jobs, business, loading } = useBiggaponSource();

    if (!loading && jobs.length > 0) return <BiggaponEditor jobs={jobs} business={business} onClose={onClose} />;

    // still loading, or no notice to make a poster of — the same window, with the message on its canvas
    return (
        <EditorModalShell
            isMobile={isMobile}
            icon={<IconSpeakerphone size={20} />}
            title={t('biggapon.pageTitle')}
            onClose={onClose}
            canvas={loading ? (
                <div role="status">
                    <IconLoader2 size={34} className="animate-spin text-zinc-500" aria-hidden="true" />
                    <span className="sr-only">{t('common.loading')}</span>
                </div>
            ) : (
                <p className="max-w-sm px-6 text-center text-sm text-zinc-400">{t('biggapon.empty')}</p>
            )}
        />
    );
}
